import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Reveal from "./Reveal";

const FAQS = [
    {
        q: "Do I need a paid TradingView plan?",
        a: "No. ITF works on free TradingView accounts. Access is granted directly to your TradingView username through the invite-only script system.",
    },
    {
        q: "Which markets and timeframes does it work on?",
        jsx: (
            <div className="space-y-3">
                <p>
                    ITF is designed for liquid, actively traded markets with defined trading sessions, and works{" "}
                    <strong className="font-bold text-slate-900">specifically on the 5-minute timeframe only</strong> — it
                    is an intraday indicator, not a positional one.
                </p>
                <p>
                    It can be used across a wide range of liquid instruments, including major indices, individual stocks,
                    futures &amp; options, ETFs such as SPY, QQQ, IWM, GLD and SLV, and indexes like DAX, CAC 40, FTSE 100,
                    Nifty 50 and Nifty Bank — along with other actively traded securities.
                </p>
                <p>
                    For the best experience, use ITF during high-volume regular market sessions where there is sufficient
                    participation and liquidity. It is not designed for 24-hour or continuously traded markets, such as
                    instruments that trade around the clock.
                </p>
                <ul className="space-y-1.5 pt-1">
                    <li><strong className="text-slate-900">Markets:</strong> liquid indices, ETFs &amp; stocks, futures &amp; options</li>
                    <li><strong className="text-slate-900">Timeframe:</strong> <strong className="font-bold text-emerald-700">5-minute only</strong></li>
                    <li><strong className="text-slate-900">Style:</strong> intraday only</li>
                    <li><strong className="text-slate-900">Best conditions:</strong> high-volume regular trading sessions</li>
                    <li><strong className="text-slate-900">Not intended for:</strong> 24-hour / continuous-session instruments</li>
                </ul>
            </div>
        ),
    },
    {
        q: "How quickly do I get access after paying?",
        a: "After checkout we verify your order and activate ITF on your TradingView account manually — usually within a few hours, and always within 24 hours. You will receive an email confirmation the moment your payment is confirmed.",
    },
    {
        q: "Is ITF financial advice or a signal service?",
        a: "No. ITF is a technical analysis tool — an indicator that displays directional market conditions on your chart. It does not provide investment, trading or financial advice or recommendations of any kind, and it does not guarantee any outcome. Every trading decision remains entirely yours.",
    },
    {
        q: "What is the refund policy?",
        a: "All sales are final to the maximum extent permitted by law, because access is digital and granted to your personal TradingView account. Please read the full Refund Policy before purchasing.",
        link: { to: "/refund-policy", label: "Read the Refund Policy" },
    },
    {
        q: "Who processes my payment?",
        a: "Payments are processed securely by Dodo Payments, our authorized reseller and Merchant of Record. Dodo Payments handles the entire transaction, applicable taxes, invoicing and your receipt — we never see or store your card details.",
    },
];

export default function Faq() {
    return (
        <section id="faq" className="relative bg-white py-20 sm:py-28" data-testid="faq-section">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <div className="text-center">
                        <p className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700">FAQ</p>
                        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                            Straight answers.
                        </h2>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
                        {FAQS.map((f, i) => (
                            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                                <AccordionTrigger
                                    data-testid={`faq-trigger-${i}`}
                                    className="text-left font-display text-base font-semibold text-slate-900 hover:no-underline"
                                >
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-sm leading-relaxed text-slate-600">
                                    {f.jsx || f.a}
                                    {f.link && (
                                        <Link to={f.link.to} className="mt-2 inline-block font-semibold text-emerald-700 hover:text-emerald-800">
                                            {f.link.label} →
                                        </Link>
                                    )}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>
            </div>
        </section>
    );
}
